import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '../lib/api';

function buildSummaryFromPositions(positions) {
  const summary = {
    totalPositions: positions.length,
    totalValue: 0,
    totalUnrealizedPnL: 0,
    totalRealizedPnL: 0,
    byStatus: {
      open: 0,
      closed: 0,
      stopped_out: 0,
      take_profit: 0
    }
  };

  for (const p of positions) {
    const status = p.status || 'open';
    if (summary.byStatus[status] === undefined) {
      summary.byStatus[status] = 0;
    }
    summary.byStatus[status] += 1;

    summary.totalValue += p.currentValue || 0;
    summary.totalUnrealizedPnL += p.unrealizedPnL || 0;
    summary.totalRealizedPnL += p.realizedPnL || 0;
  }

  return summary;
}

function tradeKey(trade) {
  if (!trade) return '';
  const id = trade._id || trade.id || trade.orderId || trade.transactionHash || trade.txHash;
  if (id) return id.toString();
  const marketId = trade?.market?.conditionId || trade?.market?.slug || '';
  const side = trade?.side || '';
  const filledAt = trade?.filledAt || trade?.createdAt || '';
  const size = trade?.filledSize || trade?.size || '';
  const price = trade?.avgFillPrice || trade?.price || '';
  return `${marketId}:${side}:${filledAt}:${size}:${price}`;
}

export const usePositionStore = create(persist(
  (set, get) => ({
    positions: [],
    trades: [],
    summary: null,
    isLoading: false,
    error: null,

    fetchPositions: async (status = 'open', showLoading = false) => {
      try {
        // Only show loading on initial fetch to avoid UI flicker on refreshes
        if (showLoading || get().positions.length === 0) {
          set({ isLoading: true, error: null });
        }
        const response = await api.get('/positions', { params: { status } });
        const positions = response.data || [];
        set({ positions, summary: buildSummaryFromPositions(positions), isLoading: false });
      } catch (error) {
        set({ error: error.response?.data?.error || 'Failed to fetch positions', isLoading: false });
      }
    },

    fetchSummary: async () => {
      try {
        const response = await api.get('/positions/summary');
        set({ summary: response.data });
      } catch (error) {
        console.error('Failed to fetch summary:', error);
      }
    },

    fetchTrades: async (params = {}) => {
      try {
        set({ isLoading: true, error: null });
        const response = await api.get('/positions/trades', { params });
        set({ trades: response.data, isLoading: false });
      } catch (error) {
        set({ error: error.response?.data?.error || 'Failed to fetch trades', isLoading: false });
      }
    },

    updateStopLoss: async (positionId, stopLossData) => {
      try {
        const response = await api.put(`/positions/${positionId}/stop-loss`, stopLossData);
        set({
          positions: get().positions.map(p => p._id === positionId ? response.data : p)
        });
        return { success: true };
      } catch (error) {
        return { success: false, error: error.response?.data?.error };
      }
    },

    updateTakeProfit: async (positionId, takeProfitData) => {
      try {
        const response = await api.put(`/positions/${positionId}/take-profit`, takeProfitData);
        set({
          positions: get().positions.map(p => p._id === positionId ? response.data : p)
        });
        return { success: true };
      } catch (error) {
        return { success: false, error: error.response?.data?.error };
      }
    },

    closePosition: async (positionId) => {
      try {
        const response = await api.post(`/positions/${positionId}/close`);
        const updatedPositions = get().positions.map(p => 
          p._id === positionId ? response.data.position : p
        );
        set({
          positions: updatedPositions,
          summary: buildSummaryFromPositions(updatedPositions)
        });
        if (response.data?.trade) {
          get().addTrade(response.data.trade);
        }
        return { success: true, data: response.data };
      } catch (error) {
        return { success: false, error: error.response?.data?.error };
      }
    },

    updatePosition: (position) => {
      const prev = get().positions;
      const idx = prev.findIndex(p => p._id === position._id);
      const updatedPositions = idx >= 0
        ? prev.map(p => p._id === position._id ? position : p)
        : [position, ...prev];
      set({
        positions: updatedPositions,
        summary: buildSummaryFromPositions(updatedPositions)
      });
    },

    addPosition: (position) => {
      const positions = [position, ...get().positions];
      set({ positions, summary: buildSummaryFromPositions(positions) });
    },

    addTrade: (trade) => {
      const key = tradeKey(trade);
      const prev = get().trades || [];
      const next = key
        ? [trade, ...prev.filter((t) => tradeKey(t) !== key)]
        : [trade, ...prev];
      set({ trades: next.slice(0, 300) });
    },

    clearError: () => {
      set({ error: null });
    }
  }),
  {
    name: 'betmimic.positionStore',
    partialize: (state) => ({
      positions: state.positions,
      trades: state.trades,
      summary: state.summary
    })
  }
));
